import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';
import { ApplicationBaseComponent } from '../../../applications/components/application-base.component';
import { ApplicationState, ApplicationStateChangeRequest, ApplicationType, InitiateWorkflowRequest, WorkflowData, WorkflowNote } from '../../../applications/models/applications.models';
import { ApplicationNotesService } from '../../../applications/services/application-notes.service';
import { ApplicationsService } from '../../../applications/services/applications.service';
import { DomainDefinitionService } from '../../../builder/services/domain-definition.service';
import { NoteType } from '../../../common/models/note-type.enum';
import { OrganizationStub } from '../../../common/models/organization.model';
import { SidePanel } from '../../../common/models/side-panel.model';
import { Well } from '../../../common/models/well.model';
import { IUploadFile } from '../../../common/services/document-management.service';
import { IssuesService } from '../../../common/services/issues.service';
import { LoaderService } from '../../../common/services/loader.service';
import { SidePanelService } from '../../../common/services/side-panel.service';
import { Dialog, DialogButton } from '../../../core/models/dialog.model';
import { SubHeader } from '../../../core/models/sub-header.model';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';
import { SubHeaderService } from '../../../core/services/sub-header.service';
import { AuthService } from '../../../services/auth.service';
import { ComplianceReportingType } from '../../models/compliance.model';
import { WellSelectorComponent } from '../well-selector/well-selector.component';

@Component({
    selector: 'app-plugging-report-submit',
    templateUrl: './plugging-report-submit.component.html',
    styleUrls: ['./plugging-report-submit.component.scss'],
    standalone: false
})
export class PluggingReportSubmitComponent extends ApplicationBaseComponent implements OnInit {
    @Input() logData: any;
    @Input() organization: OrganizationStub;
    @Input() reportType: ComplianceReportingType;
    @Input() applicationState: ApplicationState;
    @Input() savedApp: WorkflowData;

    isInReview: boolean = false;
    onBehalf: boolean = false;
    wellIsSelected: boolean = false;

    get receivedDate(): Date {
        if (this.form == null) return null;
        let ctrl = this.form.get('receivedDate');
        if (ctrl == null) return null;
        let value: Date = (ctrl.value != null && ctrl.value != '') ? new Date(ctrl.value) : null;
        return value;
    }

    get plugDate(): Date {
        if (this.form == null) return null;
        let ctrl = this.form.get('plugDate');
        if (ctrl == null) return null;
        let value: Date = (ctrl.value != null && ctrl.value != '') ? new Date(ctrl.value) : null;
        return value;
    }

    get replugDate(): Date {
        if (this.form == null) return null;
        let ctrl = this.form.get('replugDate');
        if (ctrl == null) return null;
        let value: Date = (ctrl.value != null && ctrl.value != '') ? new Date(ctrl.value) : null;
        return value;
    }

    @ViewChild(WellSelectorComponent) private _selector: WellSelectorComponent;

    allowFileManipulation: boolean = true;

    get uploadedFilesCtrl(): AbstractControl {
        if (this.form == null) return null;
        return <AbstractControl>this.form.get('uploadedFiles');
    }

    get uploadedFiles(): IUploadFile[] {
        if (this.uploadedFilesCtrl == null) return [];
        return <IUploadFile[]>this.uploadedFilesCtrl.value;
    }

    get isInCorrections(): boolean {
        if (this.application == null) return null;
        if (this.application.applicationState == ApplicationState.Corrections) return true;
        return false;
    }

    get selectedWell(): string {
        if (this.form == null) return "";
        let ctrl = this.form.get('apiNumber');
        if (ctrl == null) return "";
        return ctrl.value;
    }

    get filesUploaded(): boolean {
        let result: boolean = false;
        if (this.uploadedFiles.length == 0) {
            result = false;
        }

        else {
            result = true;
        }


        return result;

    }

    get isInternal(): boolean {
        return this.auth.User.isInternal;
    }

    get isApproved(): boolean {
        if (this.application == null) return null;
        if (this.application.applicationState == ApplicationState.Approved) return true;
        return false;
    }

    get enableWellSelection(): boolean {
        if (this.application == null) return true;
        else {
            if (this.isApproved) return false;
            if (this.isInternal && this.isInCorrections) return false;
            if (this.getFilteredNotes('apiNumber').length != 0 && this.isInCorrections) return true;
            if (this.application.applicationState == ApplicationState.Input || this.application.applicationState == ApplicationState.Start) return true;
            return false;
        }
    }


    reviewStates: ApplicationState[] = [
        ApplicationState.VerifyCompletion,
        ApplicationState.InitialReview,
        ApplicationState.InitialApproval,
        ApplicationState.FinalApproval
    ];
    get subheaderText(): string {
        return "Compliance Report #[XXX]";
    }

    subheader: SubHeader = new SubHeader({ title: "", buttons: [] });


    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    loadingObs$: Observable<boolean> = this.loading$.asObservable();

    submittedDialog: Dialog = new Dialog("Application #[APPNUMBER] has been submitted for review.", [new DialogButton("OK", true, () => { this.home(); })]);
    static saveUnsubmittedMessage: string = "Your report has been saved for completion at a later date.  For future reference, this report is stored as ID [XXX].";
    saveUnsubmittedDialog: Dialog = new Dialog("", [new DialogButton("OK", false, () => { this.navigateToSavedReport(); return true; })]);
    canceledDialog: Dialog = new Dialog("Leave report unsaved and return to home page?", [new DialogButton("Yes", true, () => { this.router.navigate(["/home"]); }), new DialogButton("No", true, () => { }),]);
    acceptedDialog: Dialog = new Dialog("Confirm that you are accepting this application?", [new DialogButton("Confirm", true, () => { this.onAcceptConfirmed(); }), new DialogButton("Cancel", true, () => { }),]);
    acceptConfirmedDialog: Dialog = new Dialog("The application has been accepted.", [new DialogButton("OK", true, () => { this.uploadDocumentsAndGoHome(); })]);


    constructor(private activatedRoute: ActivatedRoute, private builder: FormBuilder, public loader: LoaderService, public dialogService: DynamicDialogService, _subheader: SubHeaderService, _auth: AuthService, public appService: ApplicationsService,
        _sidePanelService: SidePanelService, _notesService: ApplicationNotesService, _router: Router, public domainService: DomainDefinitionService, public issuesService: IssuesService) {
        super(loader, dialogService, _subheader, _auth, appService, _router, _sidePanelService, _notesService);
        this.fileFields = ['uploadedFiles'];
        this.dateFields = ['receivedDate', 'plugDate', 'replugDate'];

    }

    ngOnInit() {
        this.form = this.buildForm();

        if (this.savedApp != null) {
           
            this.application = this.savedApp;
            this.subheader.title = this.subheaderText.replace("[XXX]", this.application.applicationNumber);
            if (this.application.applicationState != ApplicationState.Approved && this.isInternal) {
                this.subheader.buttons.push({ name: "View Notes & Timeline", method: () => this.viewTimeline() });

            }
            this.subheaderService.updateSubHeader(this.subheader);

            this.restoreWells();

            if (this.savedApp.applicationState == (ApplicationState.InitialReview || ApplicationState.Approved)) {
                this.isInReview = true;
            }


            if (this.isInternal && this.application.applicationState == ApplicationState.Corrections) {

                this.form.disable();
            }

            this.wellIsSelected = true;

            this.form = this.restoreFormData(this.form);

            let loadingObs$ = this.loadingObs$.pipe(
                delay(1000)
            ).subscribe(
                (isLoading: boolean) => {
                    if (isLoading == false) this.restoreWells();
                },
                (error) => {
                    this.handleError(error);
                });

            this.subscription.add(loadingObs$);

           

                this.loading$.next(false);

        }

     

    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    buildForm(): FormGroup {
        return this.builder.group({
            wellName: ["", []],
            wellNumber: ["", []],
            uploadedFiles: [[], []],
            organizationName: [this.organization.name, []],
            organization: [this.organization.id, []],
            rbdmsNumber: [this.organization.historicalOwnerNumber, []],
            apiNumber: ["", []],
            reportType: [this.reportType.typeId, []],
            receivedDate: ["", []],
            plugDate: ["", []],
            replugDate: ["", []],

        })

    }


    fileAdded(evt: IUploadFile): void {
      
        const files: IUploadFile[] = this.uploadedFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = "DNR - OG - FEPG-Plug Report Inspector (Form 55)"
            files.push(evt);
            this.uploadedFilesCtrl.setValue(files);
        }
    }

    onFileDeleted(evt: IUploadFile): void {
  
        const files: IUploadFile[] = this.uploadedFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.uploadedFilesCtrl.setValue(files);
        }
    }


    onWellsSelected(evt: Well[]): void {
        let selectedWell = this.form.get('apiNumber');


        if (evt.length != 0) {

            let wellName: string = evt[0].name;
            if (evt[0].lease != null && evt[0].lease.name != null && evt[0].lease.name.length > 0) wellName = evt[0].lease.name;
            else if (evt[0].wellNumber != null && evt[0].wellNumber.length > 0 && wellName != null && wellName.trim().endsWith(evt[0].wellNumber.trim())) {
                wellName = wellName.trim().replace(evt[0].wellNumber.trim(), "");
            }

            this.form.get('wellNumber').setValue(evt[0].wellNumber);
            this.form.get('wellName').setValue(wellName);
        }


        let update: boolean = false;
        evt.forEach(w => {
            if (w.id != selectedWell.value) {
                this.form.get('apiNumber').setValue(w.id);
            
                update = true;
            }
        });
        if (update) {
            if (evt.length == 0) {
                this.form.get('apiNumber').setValue(null);
            }
        }


        if (evt.length > 0) {
            this.wellIsSelected = true;

        }
        else if (evt.length == 0) {
            this.wellIsSelected = false;
        }
    }

    restoreWells(): void {
        if (this.application != null) {
            if (this._selector != null) {
                this._selector.reportedWells = [{ apiNumber: this.selectedWell, reviewStatus: "Pending" }];
                this._selector.findWells();
            }
            else {
                console.error("no selector component ready");
            }
        }
    }



    buildRequest(behalfFlag: boolean): InitiateWorkflowRequest {
        return {
            organizationId: this.organization.id,
            applicationType: ApplicationType.ComplianceReporting,
            onBehalf: behalfFlag,

        } as InitiateWorkflowRequest
    }

    checkDataBeforeSave() {


        this.form.get('reportType').setValue("PLUG");
        if (this.application != null) {

            this.form.get('rbdmsNumber').setValue(this.application.workflowFieldDatas.find(f => f.formControlName == "rbdmsNumber").stringData);
            this.form.get('organizationName').setValue(this.application.workflowFieldDatas.find(f => f.formControlName == "organizationName").stringData);
        }

        if (this.auth.User.isInternal == true) {
            this.onBehalf = true;
        }


    }

    saveForLater() {


        this.checkDataBeforeSave();

        let obs$: Observable<WorkflowData> = (this.application == null) ? this.appService.initiateApplication(this.buildRequest(this.onBehalf)).pipe(switchMap((app) => {
            this.application = app; return this.saveProgress();
        })) : this.saveProgress();
        this.loader.toggleLoader(true);

        let subs$ = obs$.pipe()
            .subscribe(
                (app) => {
                    this._loader.toggleLoader(false);

                    this.logData.id = app.applicationNumber;
                    const dialog: Dialog = this.saveUnsubmittedDialog;

                    dialog.message = PluggingReportSubmitComponent.saveUnsubmittedMessage.replace("[XXX]", app.applicationNumber);
                    dialog.toggleDialog(true);
                    this.dialogService.updateDialog(dialog);
                    this.form.markAsPristine();

                    this.allowDeactivate.next(true);
                },
                (error) => {
                    this.logError(error);
                }
            );

        this.subscription.add(subs$);

    }

    onDateUpdated(date: Date, field: string): void {
        let ctrl = this.form.get(field);
        if (ctrl != null) ctrl.setValue(date);
    }




    onAccept() {
        this.acceptedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.acceptedDialog);
    }

    onAcceptConfirmed() {


        this.checkDataBeforeSave();

        let obs$: Observable<WorkflowData> = (this.application == null) ? this.appService.initiateApplication(this.buildRequest(this.onBehalf)).pipe(switchMap((app) => {
            this.application = app; return this.saveProgress().pipe(
                switchMap((app: WorkflowData) => {
                    let destination: ApplicationState = ApplicationState.Approved;

                    let request: ApplicationStateChangeRequest = {
                        applicationId: app.id,
                        currentState: app.applicationState,
                        requestedState: destination
                    } as ApplicationStateChangeRequest;
                    return this.dataService.routeApplication(request);
                })
            );
        })) : this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let destination: ApplicationState = ApplicationState.Approved;

                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: destination
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );;

        this.loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this.loader.toggleLoader(false);
                    this.acceptConfirmedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.acceptConfirmedDialog);
                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

            );
        this.subscription.add(subs$);
    }

    submit() {

        this.checkDataBeforeSave();

        let obs$: Observable<WorkflowData> = (this.application == null) ? this.appService.initiateApplication(this.buildRequest(this.onBehalf)).pipe(switchMap((app) => {
            this.application = app; return this.saveProgress().pipe(
                switchMap((app: WorkflowData) => {
                    let destination: ApplicationState = ApplicationState.InitialReview;

                    let request: ApplicationStateChangeRequest = {
                        applicationId: app.id,
                        currentState: app.applicationState,
                        requestedState: destination
                    } as ApplicationStateChangeRequest;
                    return this.dataService.routeApplication(request);
                })
            );
        })) : this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let destination: ApplicationState = ApplicationState.InitialReview;

                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: destination
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );;

        this.loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                  
                    this.submittedDialog.message = this.submittedDialog.message.replace('#[APPNUMBER]', app.applicationNumber);
                    this.loader.toggleLoader(false);
                    this.submittedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.submittedDialog);
                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

            );
        this.subscription.add(subs$);
    }

    logError(error: any): void {
        console.error(error);
        this.loader.toggleLoader(false);
        this.dialogService.showError();
    }

    navigateToSavedReport() {
        this.router.navigate([`/issues/${this.logData.id}/resume`]);
    }

    saveConfirmed(exit: boolean): void {
        if (exit) { this.router.navigate(["/home"]); }
    }

    cancel() {
        this.canceledDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.canceledDialog);
    }

    hasFieldNotes(fieldName: string): boolean {
        return this.getFilteredNotes(fieldName).length > 0;
    }
 

    uploadDocumentsAndGoHome(): void {
        let upload$: Subscription = this.dataService.commitAssociatedDocuments(this.application.applicationNumber, false).pipe()
            .subscribe(
                () => { },
                (error) => {
                    this.handleError(error);
                }
            );
        this.subscription.add(upload$);
        setTimeout(() => { this.home(); }, 1500); //timeout before redirecting to give subscription a chance to fire
    }


}
